import React, { useState, useEffect, useRef } from 'react';
import { Bot, LogOut, ScrollText, Target, User } from 'lucide-react';
import { getTierMeta } from '../utils/tierMeta';
import { useLanguage } from '../contexts/LanguageContext';
import styles from './GameScreen.module.css';

function GameScreen({ 
  gameState, 
  socket, 
  currentRoom, 
  onLeaveGame,
  onReturnToLobby,
  isLeavingGame 
}) {
  const { t } = useLanguage();
  
  // Animation state management
  const [winningTeamAnimation, setWinningTeamAnimation] = useState(-1);
  const [trumpAccepterSlot, setTrumpAccepterSlot] = useState(null);
  const [showTrumpAnimation, setShowTrumpAnimation] = useState(false);
  const [activePostHandAnimation, setActivePostHandAnimation] = useState(null);
  const messagesEndRef = useRef(null);
  const historyScrollRef = useRef(null);
  const lastProcessedAnimationRef = useRef(null);
  const postHandAnimationTimeoutRef = useRef(null);

  // Screen size detection for responsive layout
  const [isDesktopLayout, setIsDesktopLayout] = useState(false);
  
  // State to track if user has manually scrolled up in history
  const [isUserScrollingUp, setIsUserScrollingUp] = useState(false);

  const messages = gameState?.messages;
  const playingTeam = gameState?.playingTeam;
  const trumpAccepter = gameState?.trumpAccepter;
  const acceptPhase = gameState?.acceptPhase;
  const playPhase = gameState?.playPhase;
  const handWinnerTeam = gameState?.handWinnerTeam;
  const postHandAnimation = gameState?.postHandAnimation;

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktopLayout(window.innerWidth >= 1200);
    };

    checkScreenSize(); // Check on mount
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Utility function to replace suit letters with colored symbols
  const formatSuitSymbols = (text) => {
    if (typeof text !== 'string') return text;
    const red = (symbol) => `<span class="${styles.histRed}">${symbol}</span>`;
    const black = (symbol) => `<span class="${styles.histBlack}">${symbol}</span>`;
    return text
      .replace(/([AKQJT9])S\b/g, `$1${black('♠')}`)
      .replace(/([AKQJT9])H\b/g, `$1${red('♥')}`)
      .replace(/([AKQJT9])D\b/g, `$1${red('♦')}`)
      .replace(/([AKQJT9])C\b/g, `$1${black('♣')}`)
      // Single-character suit symbols
      .replace(/\bS\b/g, black('♠'))
      .replace(/\bH\b/g, red('♥'))
      .replace(/\bD\b/g, red('♦'))
      .replace(/\bC\b/g, black('♣'));
  };

  // Effect hook to manage scrolling behavior - only scroll if user hasn't manually scrolled up
  useEffect(() => {
    const scrollContainer = historyScrollRef.current;

    // Scroll to bottom only if the user hasn't manually scrolled up
    if (scrollContainer && !isUserScrollingUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isUserScrollingUp]);

  // Effect hook to add a scroll listener to detect user scrolling
  useEffect(() => {
    const scrollContainer = historyScrollRef.current;
    
    if (scrollContainer) {
      const handleScroll = () => {
        // Check if the user has scrolled away from the bottom
        const scrollBottomPosition = scrollContainer.scrollHeight - scrollContainer.scrollTop;
        const containerHeight = scrollContainer.clientHeight;
        // Check if the scroll is within 2 pixels of the bottom (mobile tolerance)
        const atBottom = Math.abs(scrollBottomPosition - containerHeight) <= 2;
        setIsUserScrollingUp(!atBottom);
      };

      scrollContainer.addEventListener('scroll', handleScroll);

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // Trump acceptance animation detection
  useEffect(() => {
    console.log('🔍 Trump detection useEffect triggered', {
      playingTeam,
      trumpAccepter,
      trumpAccepterSlot,
      acceptPhase,
      playPhase
    });

    if (trumpAccepter !== undefined && trumpAccepter !== -1) {
      const accepterSlot = trumpAccepter;

      // Only trigger if we haven't shown animation for this player yet
      if (accepterSlot !== trumpAccepterSlot) {
        console.log(`🎉 Player ${accepterSlot} accepted trump! Playing team: ${playingTeam}`);
        setTrumpAccepterSlot(accepterSlot);
        setShowTrumpAnimation(true);

        // Remove animation after 1.9 seconds
        setTimeout(() => {
          setShowTrumpAnimation(false);
          console.log('🎭 Trump animation removed');
        }, 1900);
      }
    }
  }, [trumpAccepter, trumpAccepterSlot, playingTeam, acceptPhase, playPhase]);

  // Winning team animation detection and management
  useEffect(() => {
    if (handWinnerTeam === undefined || handWinnerTeam === -1) {
      return;
    }

    console.log('🏆 Winning team animation detected:', {
      handWinnerTeam,
      currentWinningAnimation: winningTeamAnimation
    });

    if (handWinnerTeam !== winningTeamAnimation) {
        setWinningTeamAnimation(handWinnerTeam);

        // Clear animation after 3 seconds
        setTimeout(() => {
          console.log('🏆 Clearing winning team animation');
          setWinningTeamAnimation(-1);
        }, 3000);
    }
  }, [handWinnerTeam, winningTeamAnimation]);

  // Post-hand animation detection and management (koei, geveegd, fatality)
  useEffect(() => {
    if (!postHandAnimation) {
      // Backend cleared the animation flag - ensure we reset local state + refs
      if (postHandAnimationTimeoutRef.current) {
        clearTimeout(postHandAnimationTimeoutRef.current);
        postHandAnimationTimeoutRef.current = null;
      }
      setActivePostHandAnimation(null);
      lastProcessedAnimationRef.current = null;
      return;
    }

    // Ignore duplicate events while we wait for the backend to clear the flag
    if (postHandAnimation === lastProcessedAnimationRef.current) {
      return;
    }

    console.log(`🎬 New animation detected: ${postHandAnimation}`);
    lastProcessedAnimationRef.current = postHandAnimation;
    setActivePostHandAnimation(postHandAnimation);

    if (postHandAnimationTimeoutRef.current) {
      clearTimeout(postHandAnimationTimeoutRef.current);
    }

    // Clear the local animation after it's finished playing
    postHandAnimationTimeoutRef.current = setTimeout(() => {
      setActivePostHandAnimation(null);
      postHandAnimationTimeoutRef.current = null;
    }, 2000);

    // Cleanup timeout on unmount/change
    return () => {
      if (postHandAnimationTimeoutRef.current) {
        clearTimeout(postHandAnimationTimeoutRef.current);
        postHandAnimationTimeoutRef.current = null;
      }
    };
  }, [postHandAnimation]);

  const acceptTrump = (accept) => {
    console.log('Accepting trump:', accept);
    console.log('Current room state:', currentRoom);
    console.log('Room ID being sent:', currentRoom?.roomId);
    
    if (!currentRoom?.roomId) {
      console.error('ERROR: No roomId available for trump decision!');
      alert('Error: Cannot make move - room not properly connected. Please refresh the page.');
      return;
    }
    
    socket.emit('gameMove', {
      roomId: currentRoom.roomId,
      move: {
        type: 'acceptTrump',
        accept: accept
      }
    });
  };

  const chooseTrump = (suit) => {
    console.log('Choosing trump:', suit);
    console.log('Current room state:', currentRoom);
    console.log('Room ID being sent:', currentRoom?.roomId);
    
    if (!currentRoom?.roomId) {
      console.error('ERROR: No roomId available for trump choice!');
      alert('Error: Cannot make move - room not properly connected. Please refresh the page.');
      return;
    }
    
    socket.emit('gameMove', {
      roomId: currentRoom.roomId,
      move: {
        type: 'chooseTrump',
        suit: suit
      }
    });
  };

  const playCard = (index) => {
    console.log('Playing card at index:', index);
    socket.emit('gameMove', {
      roomId: currentRoom?.roomId,
      move: {
        type: 'playCard',
        cardIndex: index
      }
    });
  };

  const getCardObject = (cardString) => {
    if (!cardString || cardString.length < 2) return null;
    return { rank: cardString.slice(0, -1), suit: cardString.slice(-1) };
  };

  const startNewGame = () => {
    // Return to lobby to start a new game (game is already over, no warning needed)
    if (onReturnToLobby) {
      onReturnToLobby();
    } else if (onLeaveGame) {
      // Fallback to onLeaveGame if onReturnToLobby is not provided
      onLeaveGame();
    }
  };

  if (!gameState || !gameState.teamScores || !gameState.userHand || !gameState.messages || !gameState.trickCards) {
    return (
      <div className={styles.gameBody}>
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingMessage}><p>{t('game.loading')}</p></div>
        </div>
      </div>
    );
  }
  
  // Show leaving game overlay if in process of leaving
  if (isLeavingGame) {
    return (
      <div className={styles.gameBody}>
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingMessage}>
            <h2>{t('game.leaving')}</h2>
            <p>{t('game.leavingHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  const myPlayerSlot = gameState.playerSlot || 0;
  const myTeamIndex = myPlayerSlot === 0 || myPlayerSlot === 2 ? 0 : 1; // My team: 0 for slots 0,2; 1 for slots 1,3
  const opponentTeamIndex = 1 - myTeamIndex;

  // Whose decision the table is waiting for, shown as the turn indicator on that seat
  const activeSlot = gameState.acceptPhase ? gameState.currentAcceptancePlayer
    : gameState.trumpPhase ? gameState.currentTrumpPlayer
    : gameState.playPhase ? gameState.currentPlayer
    : null;
  const isMyPlayTurn = gameState.playPhase && gameState.currentPlayer === gameState.playerSlot;

  // Calculate which actual players are at each visual position
  const topPlayer = getPlayerAtVisualPosition(2, myPlayerSlot);    // Visual top = position 2
  const leftPlayer = getPlayerAtVisualPosition(1, myPlayerSlot);   // Visual left = position 1
  const rightPlayer = getPlayerAtVisualPosition(3, myPlayerSlot);  // Visual right = position 3
  const bottomPlayer = getPlayerAtVisualPosition(0, myPlayerSlot); // Visual bottom = position 0 (me)

  const showsTrumpAnimation = (slot) => shouldShowTrumpAnimation(slot, trumpAccepterSlot, showTrumpAnimation, gameState);
  const seatProps = (slot, position) => ({
    className: [
      styles.player,
      styles[position],
      position !== 'bottom' && activeSlot === slot && styles.activeSeat,
      showsTrumpAnimation(slot) && styles.trumpAccepted
    ].filter(Boolean).join(' '),
    'data-trump-suit': showsTrumpAnimation(slot) ? getSuitSymbol(gameState.trump) : ''
  });
  const dealerBadge = (slot) => gameState.dealer === slot && (
    <span className={styles.dealerBadge}>{t('game.dealer')}</span>
  );
  const scoreChip = (teamIndex, label) => (
    <span
      className={[
        styles.scoreChip,
        gameState.playingTeam === teamIndex && styles.playingTeam,
        winningTeamAnimation === teamIndex && styles.winningTeamHighlight
      ].filter(Boolean).join(' ')}
    >
      <span className={styles.scoreLabel}>{label}</span>
      <b className={styles.scoreValue}>{gameState.teamScores[teamIndex]}</b>
    </span>
  );

  const sidePanels = (
    <>
      <div className={styles.history}>
        <div className={styles.historyHead}>
          <ScrollText aria-hidden="true" />
          <span>{t('game.history')}</span>
        </div>
        <div
          className={styles.historyScroll}
          ref={historyScrollRef}
          onScroll={() => {
            const scrollContainer = historyScrollRef.current;
            if (scrollContainer) {
              const atBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop === scrollContainer.clientHeight;
              setIsUserScrollingUp(!atBottom);
            }
          }}
        >
          {gameState.messages && gameState.messages.length > 0 ? (
            gameState.messages.map((msg, i) => (
              <div
                key={i}
                dangerouslySetInnerHTML={{ __html: formatSuitSymbols(typeof msg === 'string' ? msg : msg.text) }}
              />
            ))
          ) : (
            <div className={styles.historyEmpty}>{t('game.noMessages')}</div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className={styles.gameStatusBar}>
        {/* Flexible center section for decisions */}
        <div className={styles.statusSectionCenter}>
          {gameState.acceptPhase && gameState.currentAcceptancePlayer === gameState.playerSlot && (
            <div className={styles.trumpAcceptanceContainer}>
              <div className={styles.trumpQuestion}>
                <span className={styles.actionLabel}>{t('game.prompts.acceptTrump')}</span>
                <span className={`${styles.animatedTrumpSuit} ${gameState.trump ? styles[gameState.trump.toLowerCase()] : ''}`}>
                  {getSuitSymbol(gameState.trump)}
                </span>
              </div>
              <div className={styles.trumpButtons}>
                <button
                  className={`${styles.inlineBtn} ${styles.accept}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Yes button clicked');
                    acceptTrump(true);
                  }}
                >
                  {t('game.prompts.yes')}
                </button>
                <button
                  className={`${styles.inlineBtn} ${styles.decline}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('No button clicked');
                    acceptTrump(false);
                  }}
                >
                  {t('game.prompts.no')}
                </button>
              </div>
            </div>
          )}

          {gameState.trumpPhase && gameState.currentTrumpPlayer === gameState.playerSlot && (
            <div className={styles.inlineActions}>
              <span className={styles.actionLabel}>{t('game.prompts.chooseTrump')}</span>
              {['hearts', 'diamonds', 'clubs', 'spades'].map(suit => (
                <button
                  key={suit}
                  className={`${styles.trumpSuitBtn} ${styles[suit]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    chooseTrump(suit);
                  }}
                >
                  {getSuitSymbol(suit)}
                </button>
              ))}
            </div>
          )}

          {isMyPlayTurn && (
            <div className={styles.playIndicatorBig}>
              <span className={styles.bigActionText}><Target aria-hidden="true" /> {t('game.prompts.yourTurn')}</span>
            </div>
          )}

          {!gameState.acceptPhase && !gameState.trumpPhase && !gameState.playPhase && (
            <div className={styles.waitingIndicatorBig}>
              <span className={styles.bigActionText}>{t('game.waiting')}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={`${styles.gameBody} ${isDesktopLayout ? styles.desktopLayout : ''}`}>
      <div className={styles.main}>
        <header className={styles.gameHeaderBanner}>
          <div className={`ct-brand ${styles.brand}`}>
            <span className="ct-brand-mark" aria-hidden="true">K</span>
            <span className={`ct-brand-name ${styles.brandName}`}>Kwajongen</span>
          </div>
          <div className={styles.scores}>
            {scoreChip(myTeamIndex, t('game.yourTeam'))}
            {scoreChip(opponentTeamIndex, t('game.opponents'))}
          </div>
          <button
            type="button"
            onClick={onLeaveGame}
            className={`ct-btn ct-btn--outline ct-btn--inline ${styles.leaveGameBtn}`}
            aria-label={t('game.leaveGame')}
          >
            <LogOut aria-hidden="true" />
            <span>{t('game.leaveGame')}</span>
          </button>
        </header>
        <div className={styles.table}>
          {/* Face-up trump card in the top-left corner */}
          {gameState.faceUp && (
            <div className={styles.gameInfo}>
              <span className={styles.gameInfoLabel}>{t('game.trump')}</span>
              <Card card={getCardObject(gameState.faceUp)} styles={styles} />
            </div>
          )}

          <div className={styles.tableTopRow}>
            <div {...seatProps(topPlayer, 'top')}>
              {getPlayerLabel(topPlayer, myPlayerSlot, gameState, styles, t)}
              {dealerBadge(topPlayer)}
            </div>
          </div>

          <div className={styles.tableMiddleRow}>
            <div {...seatProps(leftPlayer, 'left')}>
              {getPlayerLabel(leftPlayer, myPlayerSlot, gameState, styles, t)}
              {dealerBadge(leftPlayer)}
            </div>

            <div className={styles.center}>
              <div className={styles.trickArea}>
                {[0, 1, 2, 3].map((visualPosition) => {
                  // Get which actual player sits at this visual position
                  const actualPlayerIndex = getPlayerAtVisualPosition(visualPosition, myPlayerSlot);
                  const cardStr = gameState.trickCards[actualPlayerIndex];
                  const visualClass = ['bottom', 'left', 'top', 'right'][visualPosition];
                  const isWinner = gameState.trickWinner === actualPlayerIndex;
                  
                  return (
                    <div key={visualPosition} className={`${styles.trickCardSlot} ${styles[`player${visualClass.charAt(0).toUpperCase() + visualClass.slice(1)}`]} ${isWinner ? styles.winnerCard : ''}`}>
                      {cardStr ? <Card card={getCardObject(cardStr)} styles={styles} /> : <div className={styles.emptyCardSlot} />}
                    </div>
                  );
                })}
              </div>
            </div>

            <div {...seatProps(rightPlayer, 'right')}>
              {getPlayerLabel(rightPlayer, myPlayerSlot, gameState, styles, t)}
              {dealerBadge(rightPlayer)}
            </div>
          </div>

          <div className={styles.tableBottomRow}>
            <div {...seatProps(bottomPlayer, 'bottom')}>
              <div className={`${styles.hand} ${isMyPlayTurn ? styles.handActive : ''}`}>
                {[0, 1, 2, 3].map((slotIndex) => {
                  const hasCard = gameState && gameState.userHand && gameState.userHand[slotIndex];
                  return (
                    <div key={slotIndex} className={styles.cardSlot}>
                      {hasCard ? (
                        <button
                          className={styles.cardButton}
                          onClick={() => playCard(slotIndex)}
                          disabled={!gameState.playPhase || !gameState.legalMoves.includes(slotIndex)}
                        >
                          <Card card={gameState.userHand[slotIndex]} styles={styles} />
                        </button>
                      ) : (
                        <div className={styles.emptyCardPlaceholder} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className={`${styles.selfPlate} ${activeSlot === bottomPlayer ? styles.activeSeat : ''}`}>
                {getPlayerLabel(bottomPlayer, myPlayerSlot, gameState, styles, t)}
                {dealerBadge(bottomPlayer)}
              </div>
            </div>
          </div>
        </div>

        {/* Post-Hand Animation Overlay */}
        {activePostHandAnimation && (
          <div className={`${styles.postHandAnimationOverlay} ${styles[activePostHandAnimation]}`}>
            <div className={styles.animationContent}>
              {activePostHandAnimation === 'koei' && (
                <div className={styles.koeiAnimation}>
                  <span className={styles.animationText}>KOEI!</span>
                  <span className={styles.animationEmoji}>🐄</span>
                </div>
              )}
              {activePostHandAnimation === 'geveegd' && (
                <div className={styles.geveeegdAnimation}>
                  <span className={styles.animationText}>GEVEEGD!</span>
                  <span className={styles.animationEmoji}>🧹</span>
                </div>
              )}
              {activePostHandAnimation === 'fatality' && (
                <div className={styles.fatalityAnimation}>
                  <span className={styles.animationText}>FATALITY!</span>
                  <span className={styles.animationEmoji}>💀</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Abandoned notice: the game ended because no human was left */}
        {gameState.endReason === 'abandoned' && (
          <div className={styles.winScreenOverlay}>
            <div className={styles.winScreen}>
              <div className={styles.winHeader}>
                <h1>{t('game.prompts.abandonedTitle')}</h1>
                <h2 className={styles.winnerAnnouncement}>{t('game.prompts.abandonedMessage')}</h2>
              </div>
              <div className={styles.winActions}>
                <button
                  className={styles.playAgainBtn}
                  onClick={() => socket.emit('leaveGame', { roomId: currentRoom?.roomId })}
                >
                  {t('game.prompts.backToLobby')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Win Screen Display */}
        {gameState.gameOver && (() => {
          // Determine if current player won
          const isPlayerWinner = gameState.teamScores[myTeamIndex] <= 0;

          return (
            <div className={styles.winScreenOverlay}>
              <div className={styles.winScreen}>
                {/* Trophy Animation for Winner */}
                {isPlayerWinner && (
                  <div className={styles.trophyAnimation}>
                    <div className={styles.trophyBouncing}>🏆</div>
                  </div>
                )}

                <div className={styles.winHeader}>
                  <h1>{isPlayerWinner ? t('game.prompts.congratulations') : t('game.prompts.gameOver')}</h1>
                  <h2 className={styles.winnerAnnouncement}>
                    {isPlayerWinner ? t('game.prompts.youWin') : t('game.prompts.opponentsWin')}
                  </h2>
                </div>

                <div className={styles.finalScores}>
                  <h3>{t('game.prompts.finalScores')}</h3>
                  <div className={styles.scoresDisplay}>
                    <div className={`${styles.teamScore} ${gameState.teamScores[myTeamIndex] <= 0 ? styles.winner : styles.loser}`}>
                      <span className={styles.teamLabel}>{t('game.yourTeam')}</span>
                      <span className={styles.teamPoints}>{gameState.teamScores[myTeamIndex]}</span>
                    </div>
                    <div className={`${styles.teamScore} ${gameState.teamScores[opponentTeamIndex] <= 0 ? styles.winner : styles.loser}`}>
                      <span className={styles.teamLabel}>{t('game.opponents')}</span>
                      <span className={styles.teamPoints}>{gameState.teamScores[opponentTeamIndex]}</span>
                    </div>
                  </div>
                </div>

                <div className={styles.winActions}>
                  <button className={styles.playAgainBtn} onClick={startNewGame}>
                    {t('game.prompts.playAgain')}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
      
      <div className={isDesktopLayout ? styles.desktopSidebar : styles.bottomLayout}>
        {sidePanels}
      </div>
    </div>
  );
}

// Helper function to check if a player should show trump acceptance animation
function shouldShowTrumpAnimation(playerSlot, trumpAccepterSlot, showTrumpAnimation, gameState) {
  const result = showTrumpAnimation && playerSlot === trumpAccepterSlot && gameState?.trump;
  if (result) {
    console.log(`🎯 Animation should show for player ${playerSlot}`, {
      playerSlot,
      trumpAccepterSlot,
      showTrumpAnimation,
      trump: gameState?.trump
    });
  }
  return result;
}

function getSuitSymbol(suit) {
  const symbols = { 'S': '♠', 'H': '♥', 'D': '♦', 'C': '♣' };
  return symbols[suit] || suit;
}

// Helper functions for rotating table perspective

function getPlayerAtVisualPosition(visualPosition, myPlayerSlot) {
  // Given a visual position (0=bottom, 1=left, 2=top, 3=right), 
  // return which actual player is there
  return (visualPosition + myPlayerSlot) % 4;
}

function getPlayerLabel(actualPlayerIndex, myPlayerSlot, gameState, styles, t) {
  const teamA = [0, 2];
  const isPartner = teamA.includes(myPlayerSlot) === teamA.includes(actualPlayerIndex);
  const relationship = actualPlayerIndex === myPlayerSlot ? t('game.you') : isPartner ? t('game.partner') : '';

  // Find player info for this slot
  const playerInfo = gameState.playerInfo?.find(p => p.slot === actualPlayerIndex);
  if (!playerInfo) {
    const fallbackName = t('game.playerN', { n: actualPlayerIndex + 1 });
    return <span className={styles.playerLabel}>{gameState.playerInfo ? fallbackName : `${fallbackName} ${relationship}`.trim()}</span>;
  }

  // Bot type shown under the name and as a tooltip on the icon
  const friendlyNames = {
    smart: 'smart',
    aggressive: 'aggro',
    conservative: 'beginner',
    veteran: 'veteran',
    master: 'master',
    grandmaster: 'grandmaster',
    jev: 'jev'
  };
  const botType = playerInfo.botType || 'smart';
  const botTypeKey = botType.charAt(0).toUpperCase() + botType.slice(1);
  const iconTitle = playerInfo.isBot ? (friendlyNames[botType] || friendlyNames.smart) : '';
  
  const badgeMeta = getTierMeta(playerInfo.challengeTier || 0);
  const badgeLabel = t ? (t(badgeMeta.labelKey) || badgeMeta.defaultLabel) : badgeMeta.defaultLabel;
  const badge = playerInfo.isBot ? null : (
    <span
      className={`tier-badge tier-badge-${badgeMeta.level} tier-badge--small`}
      title={badgeLabel}
      style={{ marginLeft: '4px' }}
    >
      {badgeMeta.icon}
    </span>
  );

  const displayName = playerInfo.isBot ? (
    <span className={styles.botIdentity}>
      <span>{playerInfo.username}</span>
      <span className={styles.botTypeLabel}>{iconTitle} {relationship}</span>
    </span>
  ) : <>{playerInfo.username} {badge} {relationship}</>;

  // Determine color class for bots
  const colorClass = playerInfo.isBot && playerInfo.botType ? styles[`botName${botTypeKey}`] : '';

  return (
    <span className={`${styles.playerLabel} ${colorClass || ''}`.trim()}>
      <span
        className={`${styles.seatIcon} ${playerInfo.isBot ? styles[`botIcon${botTypeKey}`] : ''}`.trim()}
        data-tooltip={iconTitle || undefined}
      >
        {playerInfo.isBot ? <Bot aria-hidden="true" /> : <User aria-hidden="true" />}
      </span> {displayName}
    </span>
  );
}

function getDisplayRank(rank) {
  return rank === 'T' ? '10' : rank;
}

function Card({ card, small, styles }) {
  if (!card) return null;
  const suitSymbol = getSuitSymbol(card.suit);
  const color = (card.suit === 'H' || card.suit === 'D') ? 'red' : 'black';
  const displayRank = getDisplayRank(card.rank);
  return (
    <div className={`${styles.card} ${styles[color]} ${small ? styles.small : ''}`}>
      <div className={styles.rank}>{displayRank}</div>
      <div className={styles.suit}>{suitSymbol}</div>
      <div className={styles.bottomRank}>{displayRank}</div>
    </div>
  );
}

export default GameScreen;
